import { useCallback, useEffect, useState, useRef } from 'react';
import { API, Auth } from 'aws-amplify';
import { getAuthToken } from '../utils/AuthUtils';
import DashboardDownloadConsole from './DashboardDownloadConsole/DashboardDownloadConsole';
import Top5DashboardIndexV3View from './Top5DashboardIndexV3View';

const BENCHMARKS = Object.freeze({
    "CEO": "Chief Executive Officer",
    "CFO": "Chief Financial Officer",
    "EXCH": "Executive Chair of the Board",
    "VCH": "Executive Vice Chair of the Board",
    "COO": "Chief Operating Officer",
    "OPS": "Top Operations",
    "HR": "Top Human Resources",
    "GC": "Chief Legal Officer",
    "FIN": "Second Level Finance",
    "MAR": "Top Marketing",
    "SALE": "Top Sales",
    "PDIV": "Division Head",
    "CADO": "Top Administrative",
    "CAO": "Top Accounting",
    "CIO": "Top Information / IT",
    "CTO": "Top Technical / Technology",
    "CSO": "Top Scientific",
    "RD": "Top Research & Development",
    "CMO": "Top Medical",
    "IR": "Top Investor Relations",
    "BD": "Top Business Development",
    "CD": "Top Corporate Development",
    "RISK": "Top Risk",
    "LOAN": "Top Lending",
    "WM": "Top Wealth Management",
    "INV": "Top Investment"
})

const templateId = "50135377-3de2-4a94-85f1-a1bbb68e6c0f";

const Top5DashboardIndexDataConsole = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [errorDisplayStatus, setErrorDisplayStatus] = useState(false);
    const [indexSummary, setIndexSummary] = useState(null);
    const [showDashboardDownloadConsole, setShowDashboardDownloadConsole] = useState(false);
    const [roleCode, setRoleCode] = useState("CEO");
    const [index, setIndex] = useState("S&P_500");
    const downloadLinkRef = useRef(null);
    const dashboardDataCacheRef = useRef(new Map());

    const handleSetData = (data) => {
        setIndexSummary(data.indexSummary ?? null);
    };

    const handleExportData = async () => {
        const XLSX = await import("xlsx");
        const wb = XLSX.utils.book_new();
        const sectorSummaries = indexSummary?.sectorSummaries ?? [];
        const overviewRows = sectorSummaries.map((summary) => ({
            Sector: summary.sector,
            Companies: summary.companyCount,
            Transitions: summary.transitionCount,
            'Companies with Special Awards': summary.specialCompanyCount,
            'Salary Pay Mix': summary.payMix?.salary,
            'Bonus Pay Mix': summary.payMix?.bonus,
            'LTI Pay Mix': summary.payMix?.lti,
        }));
        const payLevelRows = sectorSummaries.flatMap((summary) => Object.entries(summary.payLevels ?? {}).map(([element, values]) => ({
            Sector: summary.sector, Element: element, Count: values.count, P25: values.p25, Median: values.median, P75: values.p75,
        })));
        const elementRows = sectorSummaries.flatMap((summary) => Object.entries(summary.elements ?? {}).map(([element, values]) => ({
            Sector: summary.sector, Element: element, Count: values.count, Median: values.median, 'Median YoY': values.medianYoy,
            'Median YoY Count': values.yoyCount, 'Median Dollar Change': values.medianDollarChange,
            'Incumbent Median YoY': values.incumbentMedianYoy, 'Incumbent YoY Count': values.incumbentYoyCount,
            'Incumbent Median Dollar Change': values.incumbentMedianDollarChange,
            P10: values.distribution?.p10, P25: values.distribution?.p25, P50: values.distribution?.median,
            P75: values.distribution?.p75, P90: values.distribution?.p90,
        })));
        const trendRows = sectorSummaries.flatMap((summary) =>
            Object.entries(summary.elements ?? {}).flatMap(([element, values]) =>
                (values.trend ?? []).map((point) => ({
                    Sector: summary.sector, Element: element, Period: point.period, Median: point.value, 'Comparable Company Median YoY': point.yoy,
                })),
            ),
        );
        const transitionAnalysisRows = sectorSummaries.flatMap((summary) =>
            Object.entries(summary.elements ?? {}).flatMap(([element, values]) =>
                (values.transitions ?? []).map((transition) => ({
                    Sector: summary.sector, Element: element, 'Transition Type': transition.transitionType, Companies: transition.companyCount,
                    Median: transition.median, 'Median YoY': transition.medianYoy, 'Companies with Special Awards': transition.specialAwardCount,
                    'Special Award Prevalence': transition.specialAwardPercent,
                })),
            ),
        );
        const awardTypeRows = sectorSummaries.flatMap((summary) =>
            Object.entries(summary.elements ?? {}).flatMap(([element, values]) =>
                (values.transitions ?? []).flatMap((transition) =>
                    (transition.awardTypes ?? []).map((awardType) => ({
                        Sector: summary.sector, Element: element, 'Transition Type': transition.transitionType, 'Award Type': awardType.grantType,
                        Companies: awardType.companyCount, Prevalence: awardType.percent,
                    })),
                ),
            ),
        );

        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(overviewRows), 'Overview');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(payLevelRows), 'Pay_Levels');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(elementRows), 'Element_Statistics');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(trendRows), 'Three_Year_Trends');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(transitionAnalysisRows), 'Transition_Analysis');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(awardTypeRows), 'Award_Type_Prevalence');

        XLSX.writeFile(wb, `${roleCode}_Pay_Dashboard_Data.xlsx`)
    }

    const checkDataAccess = async () => {
        const user = await Auth.currentAuthenticatedUser();
        let userGroups = user.signInUserSession.accessToken.payload["cognito:groups"];
        return userGroups?.some(group => ['FWC_Admin', 'Internal', 'Client'].includes(group));
    }

    const fetchData = useCallback(async () => {
        setError("");
        setErrorDisplayStatus(false);

        const cacheKey = `${templateId}:${roleCode}:${index}`;
        const cachedData = dashboardDataCacheRef.current.get(cacheKey);
        if (cachedData) {
            handleSetData(cachedData);
            setLoading(false);
            return;
        }

        setLoading(true);

        const queryStringParameters = {
            templateId,
            index
        }

        const dataAccess = await checkDataAccess();
        if (!dataAccess) {
            setError("You do not have access to view this data. Please reachout to data@fwcook.com to request access.");
            setErrorDisplayStatus(true);
            setLoading(false);
            return;
        }

        const authToken = await getAuthToken();
        API.get("AnnualCompensations", `/dashboard-data/index/${roleCode}`, {
            headers: {
                'Token': authToken
            },
            queryStringParameters
        }).then(response => {
            dashboardDataCacheRef.current.set(cacheKey, response);
            handleSetData(response);
            setErrorDisplayStatus(false);
            setLoading(false);
        }).catch(fetchError => {
            console.log(fetchError);
            setError("Error retrieving dashboard data");
            setErrorDisplayStatus(true);
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleCode, index]);

    useEffect(() => {
        setLoading(false);
        setError("");
        setErrorDisplayStatus(false);
        setIndexSummary(null);
        fetchData();
        setShowDashboardDownloadConsole(false);
    }, [fetchData, roleCode, index]);

    const downloadDocument = async (docType) => {
        setErrorDisplayStatus(false);
        const url = '/relevant-documents';
        const authToken = await getAuthToken();
        const queryStringParameters = {
            docType,
            templateId
        }
        API.get('AnnualCompensations', url, {
            headers: {
                'Token': authToken
            },
            queryStringParameters
        }).then((response) => {
            if (downloadLinkRef.current) {
                downloadLinkRef.current.href = response.url;
                downloadLinkRef.current.click();
            }
        }).catch((error) => {
            if (error !== undefined && error.response !== undefined &&
                error.response.data !== undefined && error.response.data.message !== undefined) {
                console.log(error.response.data.message);
                setError(error.response.data.message);
            } else {
                console.log("An error occurred while retrieving file. Please try again later.");
                setError("An error occurred while retrieving file. Please try again later.");
            }
            setErrorDisplayStatus(true);
        });
    }

    return (
        <>
            <Top5DashboardIndexV3View
                indexSummary={indexSummary}
                loading={loading}
                error={error}
                errorDisplayStatus={errorDisplayStatus}
                index={index}
                setIndex={setIndex}
                roleCode={roleCode}
                setRoleCode={setRoleCode}
                benchmarks={BENCHMARKS}
                onDownload={() => setShowDashboardDownloadConsole(true)}
                onMethodology={() => downloadDocument("top5DashboardMethodology")}
            />
            <a href="#download" ref={downloadLinkRef} download id="download-relevant-doc" hidden>Download</a>
            <DashboardDownloadConsole
                show={showDashboardDownloadConsole}
                onClose={() => setShowDashboardDownloadConsole(false)}
                handleDownloadData={() => handleExportData()}
                templateName={`${roleCode} Pay Data`}
                templateId={`${roleCode}_PAY_DATA_DASHBOARD`}
            />
        </>
    );
};

export default Top5DashboardIndexDataConsole;
